import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useQueryClient, useMutation } from '@tanstack/react-query';
import {
    MdArrowBack, MdBlock, MdCheckCircle, MdHourglassEmpty,
    MdEdit, MdPayment, MdStorage, MdPeople, MdDirectionsBoat
} from 'react-icons/md';
import { Modal } from '../../components/shared/Modal';
import { useToast } from '../../components/shared/Toast';
import { LicenseForm } from './LicenseForm';
import { cn } from '../../lib/utils';
import { apiFetch } from '../../utils/api';
import { PageSkeleton } from '@euroasiann/shared/ui';

const API_URL = import.meta.env.VITE_API_URL || (import.meta.env.DEV ? '' : 'http://localhost:3000');

interface LicenseData {
    _id: string;
    licenseKey: string;
    status: 'active' | 'expired' | 'suspended' | 'revoked';
    licenseType: string;
    planType: string;
    expiryDate: string;
    organizationId: string;
    organizationName: string;
    enabledModules: string[];
    maxUsers: number;
    maxVessels: number;
    maxItems: number;
    usageLimits?: {
        users: number;
        vessels: number;
        items: number;
        employees: number;
        storage: number;
    };
    currentUsage: {
        users: number;
        vessels: number;
        items: number;
        employees: number;
        storage?: {
            used: number;
            limit: number;
        };
    };
    paymentStatus?: string;
}

export function LicenseDetailPage() {
    const { licenseId } = useParams();
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { showToast } = useToast();

    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
    const [extendDate, setExtendDate] = useState('');

    // Fetch single license
    const { data: license, isLoading, error } = useQuery<LicenseData>({
        queryKey: ['tech-license-detail', licenseId],
        queryFn: async () => {
            const response = await apiFetch(`/api/v1/tech/licenses/${licenseId}`);
            if (!response.ok) throw new Error('Failed to fetch product license');
            const result = await response.json();
            return result.data;
        }
    });

    // Action Mutations
    const updateStatusMutation = useMutation({
        mutationFn: async (status: string) => {
            const response = await apiFetch(`/api/v1/tech/licenses/${licenseId}/status`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ status })
            });
            if (!response.ok) {
                const err = await response.json();
                throw new Error(err.error || 'Failed to update status');
            }
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tech-license-detail', licenseId] });
            queryClient.invalidateQueries({ queryKey: ['tech-licenses-list'] });
            showToast('License status updated', 'success');
        },
        onError: (err: any) => showToast(err.message, 'error')
    });

    const extendExpiryMutation = useMutation({
        mutationFn: async (date: string) => {
            const response = await apiFetch(`/api/v1/tech/licenses/${licenseId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ expiryDate: date })
            });
            if (!response.ok) throw new Error('Failed to extend expiry');
            return response.json();
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['tech-license-detail', licenseId] });
            setIsExtendModalOpen(false);
            showToast('License expiry extended', 'success');
        },
        onError: (err: any) => showToast(err.message, 'error')
    });

    // Handlers
    const handleToggleSuspend = () => {
        if (!license) return;
        const newStatus = license.status === 'suspended' ? 'ACTIVE' : 'SUSPENDED';
        if (confirm(`Are you sure you want to ${newStatus} this license?`)) {
            updateStatusMutation.mutate(newStatus);
        }
    };

    const handleExtend = () => {
        if (!license) return;
        const current = new Date(license.expiryDate);
        current.setDate(current.getDate() + 30);
        setExtendDate(current.toISOString().split('T')[0]);
        setIsExtendModalOpen(true);
    };

    if (isLoading) {
        return (
            <div className="p-8">
                <PageSkeleton variant="page" />
            </div>
        );
    }

    if (error || !license) {
        return (
            <div className="p-8 text-center text-red-500">
                <p>Failed to load license details.</p>
                <button onClick={() => navigate('/licenses')} className="mt-4 text-emerald-600 hover:underline">
                    Return to Licenses
                </button>
            </div>
        );
    }

    // Value Extractors for consistent fallbacks
    const usersLimit = license.usageLimits?.users || license.maxUsers || 0;
    const usersUsed = license.currentUsage?.users || 0;
    const vesselsLimit = license.usageLimits?.vessels || license.maxVessels || 0;
    const vesselsUsed = license.currentUsage?.vessels || 0;
    const storageLimit = license.usageLimits?.storage || license.currentUsage?.storage?.limit || 0;
    const storageUsed = license.currentUsage?.storage?.used || 0;

    const statusColors: Record<string, string> = {
        active: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/50 dark:text-emerald-300',
        expired: 'bg-red-100 text-red-800 dark:bg-red-900/50 dark:text-red-300',
        suspended: 'bg-amber-100 text-amber-800 dark:bg-amber-900/50 dark:text-amber-300',
        revoked: 'bg-[#0088cc]/10 text-[hsl(var(--foreground))] dark:bg-[#0088cc]/18 dark:text-[#0088cc]/45'
    };

    return (
        <div className="w-full max-w-5xl mx-auto space-y-6">
            {/* Header & Navigation */}
            <div className="flex items-center justify-between mb-8">
                <div className="flex items-center gap-4">
                    <button
                        onClick={() => navigate('/licenses')}
                        className="p-2 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-lg hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/18 transition-colors"
                    >
                        <MdArrowBack className="w-5 h-5 text-[hsl(var(--muted-foreground))]" />
                    </button>
                    <div>
                        <h1 className="text-2xl font-bold text-[hsl(var(--foreground))]">License Details</h1>
                        <p className="text-sm text-[hsl(var(--muted-foreground))] font-mono mt-1">{license.licenseKey}</p>
                    </div>
                </div>

                {/* Action Buttons */}
                <div className="flex items-center gap-3">
                    <button
                        onClick={handleExtend}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/18"
                    >
                        <MdHourglassEmpty className="w-4 h-4" /> Extend
                    </button>
                    <button
                        onClick={() => setIsEditModalOpen(true)}
                        className="flex items-center gap-2 px-3 py-2 text-sm font-semibold rounded-lg border border-[hsl(var(--border))] bg-[hsl(var(--card))] text-[hsl(var(--foreground))] hover:bg-[#0088cc]/5 dark:hover:bg-[#0088cc]/18"
                    >
                        <MdEdit className="w-4 h-4" /> Modify Plan
                    </button>
                    <button
                        onClick={handleToggleSuspend}
                        disabled={updateStatusMutation.isPending}
                        className={cn(
                            "flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-lg text-white",
                            license.status === 'suspended' ? "bg-emerald-600 hover:bg-emerald-700" : "bg-red-600 hover:bg-red-700"
                        )}
                    >
                        {license.status === 'suspended' ? <MdCheckCircle className="w-4 h-4" /> : <MdBlock className="w-4 h-4" />}
                        {license.status === 'suspended' ? 'Activate License' : 'Suspend License'}
                    </button>
                </div>
            </div>

            {/* Main Info Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {/* Org & Plan Info */}
                <div className="col-span-1 md:col-span-2 bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-6 shadow-sm">
                    <h2 className="text-lg font-bold mb-4 border-b border-[hsl(var(--border))] pb-2">Organization Overview</h2>
                    <div className="grid grid-cols-2 gap-y-4 text-sm mt-4">
                        <div>
                            <p className="text-[hsl(var(--muted-foreground))] mb-1">Organization</p>
                            <p className="font-semibold text-lg">{license.organizationName || 'Unknown'}</p>
                        </div>
                        <div>
                            <p className="text-[hsl(var(--muted-foreground))] mb-1">Plan Type</p>
                            <p className="font-semibold uppercase bg-blue-50 text-[#0077b3] dark:bg-blue-900/30 dark:text-blue-300 inline-block px-2 py-0.5 rounded">
                                {license.planType || license.licenseType}
                            </p>
                        </div>
                        <div>
                            <p className="text-[hsl(var(--muted-foreground))] mb-1">Payment Status</p>
                            <p className="font-semibold flex items-center gap-1">
                                <MdPayment className="w-4 h-4 text-[#0088cc]/65" />
                                {license.paymentStatus || 'PAID'}
                            </p>
                        </div>
                        <div>
                            <p className="text-[hsl(var(--muted-foreground))] mb-1">Current Status</p>
                            <span className={cn('px-2 py-1 rounded-full text-xs font-bold uppercase inline-block', statusColors[license.status] || statusColors.active)}>
                                {license.status}
                            </span>
                        </div>
                    </div>
                </div>

                {/* Expiry Info */}
                <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-6 shadow-sm flex flex-col justify-center items-center text-center">
                    <div className="w-16 h-16 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-4">
                        <MdHourglassEmpty className="w-8 h-8 text-[#0088cc]" />
                    </div>
                    <p className="text-[hsl(var(--muted-foreground))] font-medium mb-1">Expires On</p>
                    <p className={cn(
                        "text-2xl font-bold",
                        new Date(license.expiryDate) < new Date() ? "text-red-600" : "text-[hsl(var(--foreground))]"
                    )}>
                        {new Date(license.expiryDate).toLocaleDateString(undefined, {
                            year: 'numeric', month: 'long', day: 'numeric'
                        })}
                    </p>
                    <div className="mt-4">
                        <p className="text-xs text-[hsl(var(--muted-foreground))]">
                            {Math.max(0, Math.ceil((new Date(license.expiryDate).getTime() - new Date().getTime()) / (1000 * 3600 * 24)))} days remaining
                        </p>
                    </div>
                </div>
            </div>

            {/* Usage Analytics */}
            <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-bold mb-6 border-b border-[hsl(var(--border))] pb-2">Usage & Limits</h2>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    {/* Users */}
                    <div className="space-y-2">
                        <div className="flex justify-between items-end">
                            <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] font-medium">
                                <MdPeople className="w-5 h-5 text-emerald-500" /> Users
                            </div>
                            <span className="text-sm font-semibold">{usersUsed} / {usersLimit === 0 ? 'Unlimited' : usersLimit}</span>
                        </div>
                        <div className="h-2 w-full bg-[#0088cc]/10 dark:bg-[#0088cc]/18 rounded-full overflow-hidden">
                            <div
                                className={cn("h-full", usersLimit > 0 && (usersUsed / usersLimit) > 0.9 ? 'bg-red-500' : 'bg-emerald-500')}
                                style={{ width: usersLimit === 0 ? '100%' : `${Math.min((usersUsed / usersLimit) * 100, 100)}%` }}
                            />
                        </div>
                    </div>

                    {/* Vessels */}
                    <div className="space-y-2">
                        <div className="flex justify-between items-end">
                            <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] font-medium">
                                <MdDirectionsBoat className="w-5 h-5 text-[#0088cc]" /> Vessels
                            </div>
                            <span className="text-sm font-semibold">{vesselsUsed} / {vesselsLimit === 0 ? 'Unlimited' : vesselsLimit}</span>
                        </div>
                        <div className="h-2 w-full bg-[#0088cc]/10 dark:bg-[#0088cc]/18 rounded-full overflow-hidden">
                            <div
                                className={cn("h-full", vesselsLimit > 0 && (vesselsUsed / vesselsLimit) > 0.9 ? 'bg-red-500' : 'bg-[#0088cc]')}
                                style={{ width: vesselsLimit === 0 ? '100%' : `${Math.min((vesselsUsed / vesselsLimit) * 100, 100)}%` }}
                            />
                        </div>
                    </div>

                    {/* Storage */}
                    <div className="space-y-2">
                        <div className="flex justify-between items-end">
                            <div className="flex items-center gap-2 text-[hsl(var(--muted-foreground))] font-medium">
                                <MdStorage className="w-5 h-5 text-purple-500" /> Storage (GB)
                            </div>
                            <span className="text-sm font-semibold">{storageUsed} / {storageLimit === 0 ? 'Unlimited' : storageLimit}</span>
                        </div>
                        <div className="h-2 w-full bg-[#0088cc]/10 dark:bg-[#0088cc]/18 rounded-full overflow-hidden">
                            <div
                                className={cn("h-full", storageLimit > 0 && (storageUsed / storageLimit) > 0.9 ? 'bg-red-500' : 'bg-purple-500')}
                                style={{ width: storageLimit === 0 ? '100%' : `${Math.min((storageUsed / storageLimit) * 100, 100)}%` }}
                            />
                        </div>
                    </div>
                </div>
            </div>

            {/* Modules */}
            <div className="bg-[hsl(var(--card))] border border-[hsl(var(--border))] rounded-xl p-6 shadow-sm">
                <h2 className="text-lg font-bold mb-4 border-b border-[hsl(var(--border))] pb-2">Enabled Modules</h2>
                {license.enabledModules && license.enabledModules.length > 0 ? (
                    <div className="flex flex-wrap gap-2">
                        {license.enabledModules.map(mod => (
                            <span key={mod} className="px-3 py-1 bg-[#0088cc]/10 dark:bg-[#0088cc]/18 border border-[#0088cc]/20 dark:border-[#0088cc]/20 rounded-lg text-sm font-medium capitalize">
                                {mod.replace('-', ' ')}
                            </span>
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-[hsl(var(--muted-foreground))] italic">No specific modules configured for this license.</p>
                )}
            </div>

            {/* Modals */}
            {isEditModalOpen && (
                <Modal title="Edit License Profile" isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)}>
                    <LicenseForm
                        license={license as any}
                        onSuccess={() => { setIsEditModalOpen(false); queryClient.invalidateQueries({ queryKey: ['tech-license-detail', licenseId] }); }}
                        onCancel={() => setIsEditModalOpen(false)}
                    />
                </Modal>
            )}

            {isExtendModalOpen && (
                <Modal title="Extend License Expiry" isOpen={isExtendModalOpen} onClose={() => setIsExtendModalOpen(false)}>
                    <div className="p-4 space-y-4">
                        <p className="text-sm text-[hsl(var(--muted-foreground))] dark:text-[#0088cc]/65">
                            Current Expiry: <span className="font-semibold">{new Date(license.expiryDate).toLocaleDateString()}</span>
                        </p>
                        <div>
                            <label className="block text-sm font-medium mb-1">New Expiry Date</label>
                            <input
                                type="date"
                                value={extendDate}
                                onChange={e => setExtendDate(e.target.value)}
                                className="w-full px-3 py-2 border rounded-lg dark:bg-[#0088cc]/18 dark:border-[#0088cc]/20"
                            />
                        </div>
                        <div className="flex justify-end gap-2 mt-4">
                            <button onClick={() => setIsExtendModalOpen(false)} className="px-4 py-2 text-[hsl(var(--muted-foreground))] hover:bg-[#0088cc]/10 rounded-lg">Cancel</button>
                            <button
                                onClick={() => extendExpiryMutation.mutate(new Date(extendDate).toISOString())}
                                className="px-4 py-2 bg-[#0088cc] text-white rounded-lg hover:bg-[#0077b3]"
                                disabled={extendExpiryMutation.isPending}
                            >
                                {extendExpiryMutation.isPending ? 'Saving...' : 'Confirm Extension'}
                            </button>
                        </div>
                    </div>
                </Modal>
            )}

        </div>
    );
}


